import React, { useEffect, useState } from "react";
import { apiFetch } from "../api/apiFetch";
import { Navigate } from "react-router-dom";
import ProductTable from "../components/ProductTable";
import ErrorModal from "../components/ErrorModal";
import TriStateFilter from "../components/TriStateFilter";
import { usePermission } from "../hooks/usePermission";
import { exportProductLabelsToPDF } from "../utils/productLabelsPdf";

export default function ProductList({ user }) {
  const { hasPermission } = usePermission();
  const canCreate = hasPermission("products.create");
  const canEdit = hasPermission("products.edit");
  const canDelete = hasPermission("products.delete");

  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [search, setSearch] = useState("");
  const [brandFilter, setBrandFilter] = useState("");
  const [activeFilter, setActiveFilter] = useState("only"); // "all", "only", "exclude" - default: only active
  const [toBeSoldFilter, setToBeSoldFilter] = useState("only"); // "all", "only", "exclude" - default: only sellable
  const [sortColumn, setSortColumn] = useState("product_name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [marginSortType, setMarginSortType] = useState("percentage"); // 'percentage' or 'absolute'
  const [unauthorized, setUnauthorized] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [deletingProduct, setDeletingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    brand_id: "",
    description: "",
    is_to_be_sold: false,
    selling_price_excl_vat: "",
    selling_price_incl_vat: "",
    purchase_price_excl_discount: "",
    purchase_price_incl_discount: "",
    is_active: true,
  });
  const [loading, setLoading] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [errorModal, setErrorModal] = useState({ show: false, message: "" });
  const [exportingStickers, setExportingStickers] = useState(false);
  const [showStickersModal, setShowStickersModal] = useState(false);
  const [allProductsForStickers, setAllProductsForStickers] = useState([]);
  const [selectedBrands, setSelectedBrands] = useState({});

  const fetchProducts = () => {
    setLoadingProducts(true);
    const params = new URLSearchParams({
      search,
      brand: brandFilter,
      sort: sortColumn,
      order: sortOrder,
      limit: 1000,  // Fetch up to 1000 products
    });

    // Handle active filter (tri-state)
    if (activeFilter === "all") {
      params.append("include_inactive", "true"); // Show all (active + inactive)
    } else if (activeFilter === "only") {
      params.append("include_inactive", "false"); // Show only active
      params.append("is_active", "true");
    } else if (activeFilter === "exclude") {
      params.append("include_inactive", "true");
      params.append("is_active", "false"); // Show only inactive
    }

    // Handle to_be_sold filter (tri-state)
    if (toBeSoldFilter === "only") {
      params.append("is_to_be_sold", "true");
    } else if (toBeSoldFilter === "exclude") {
      params.append("is_to_be_sold", "false");
    }
    // "all" = don't add is_to_be_sold param

    apiFetch(`/products?${params}`)
      .then((data) => {
        // Handle new paginated response format
        if (data.items) {
          setProducts(data.items);
        } else {
          // Fallback for old format (shouldn't happen)
          setProducts(data);
        }
      })
      .catch((err) => {
        console.error("Fout bij ophalen producten:", err);
        if (err.message.includes("403")) setUnauthorized(true);
      })
      .finally(() => setLoadingProducts(false));
  };

  useEffect(() => {
    if (!user) return;
    apiFetch("/brands")
      .then(setBrands)
      .catch((err) => {
        console.error("Fout bij ophalen merken:", err);
        if (err.message.includes("403")) setUnauthorized(true);
      });
  }, [user]);

  useEffect(() => {
    if (!user) return;
    fetchProducts();
  }, [user, search, brandFilter, sortColumn, sortOrder, activeFilter, toBeSoldFilter]);

  const toggleSort = (col, marginType) => {
    // Handle margin column with dual sort types
    if (col === "margin" && marginType) {
      if (sortColumn === "margin" && marginSortType === marginType) {
        // Same column and type - toggle order
        setSortOrder(sortOrder === "asc" ? "desc" : "asc");
      } else {
        // Different column or type - set new sort
        setSortColumn("margin");
        setMarginSortType(marginType);
        setSortOrder("desc"); // Default descending for margin
      }
    } else if (col === sortColumn) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(col);
      setSortOrder("asc");
    }
  };

  const clearFilters = () => {
    setSearch("");
    setBrandFilter("");
    setActiveFilter("only");
    setToBeSoldFilter("only");
  };

  // Check if filters differ from defaults (search empty, brand all, toBeSold=only, active=only)
  const hasActiveFilters = search || brandFilter || activeFilter !== "only" || toBeSoldFilter !== "only";

  // Check if a product matches current filter criteria
  const productMatchesFilters = (product) => {
    // Check active filter
    if (activeFilter === "only" && !product.is_active) return false;
    if (activeFilter === "exclude" && product.is_active) return false;

    // Check to_be_sold filter
    if (toBeSoldFilter === "only" && !product.is_to_be_sold) return false;
    if (toBeSoldFilter === "exclude" && product.is_to_be_sold) return false;

    return true;
  };

  // Calculate percentage margin for a product (using selling_price_excl_vat)
  const calculatePercentageMargin = (product) => {
    const sellingPrice = product.selling_price_excl_vat;
    const purchasePrice = product.purchase_price_incl_discount;
    if (sellingPrice == null || purchasePrice == null || purchasePrice === 0) {
      return null;
    }
    return ((sellingPrice - purchasePrice) / purchasePrice) * 100;
  };

  // Sort products - handle margin percentage sorting client-side
  const getSortedProducts = () => {
    if (sortColumn !== "margin") {
      // For non-margin columns, use the backend-sorted order
      return products;
    }

    // For margin column, sort client-side based on margin type
    const sortedProducts = [...products].sort((a, b) => {
      let valA, valB;

      if (marginSortType === "percentage") {
        valA = calculatePercentageMargin(a);
        valB = calculatePercentageMargin(b);
      } else {
        // absolute - calculate from selling_price_excl_vat and purchase_price_incl_discount
        valA = (a.selling_price_excl_vat != null && a.purchase_price_incl_discount != null) ? a.selling_price_excl_vat - a.purchase_price_incl_discount : null;
        valB = (b.selling_price_excl_vat != null && b.purchase_price_incl_discount != null) ? b.selling_price_excl_vat - b.purchase_price_incl_discount : null;
      }

      // Handle null values - put them at the end
      if (valA == null && valB == null) return 0;
      if (valA == null) return 1;
      if (valB == null) return -1;

      if (sortOrder === "asc") {
        return valA - valB;
      } else {
        return valB - valA;
      }
    });

    return sortedProducts;
  };

  const handleAdd = () => {
    setEditingProduct(null);
    setFormData({
      name: "",
      brand_id: "",
      description: "",
      is_to_be_sold: false,
      selling_price_excl_vat: "",
      selling_price_incl_vat: "",
      purchase_price_excl_discount: "",
      purchase_price_incl_discount: "",
      is_active: true,
    });
    setShowModal(true);
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.product_name,
      brand_id: String(product.brand_id),
      description: product.description || "",
      is_to_be_sold: product.is_to_be_sold || false,
      selling_price_excl_vat: product.selling_price_excl_vat != null ? String(product.selling_price_excl_vat) : "",
      selling_price_incl_vat: product.selling_price_incl_vat != null ? String(product.selling_price_incl_vat) : "",
      purchase_price_excl_discount: product.purchase_price_excl_discount != null ? String(product.purchase_price_excl_discount) : "",
      purchase_price_incl_discount: product.purchase_price_incl_discount != null ? String(product.purchase_price_incl_discount) : "",
      is_active: product.is_active !== undefined ? product.is_active : true,
    });
    setShowModal(true);
  };

  const handleDelete = (product) => {
    setDeletingProduct(product);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    if (!deletingProduct) return;

    setLoading(true);
    apiFetch(`/products/${deletingProduct.id}`, { method: "DELETE" })
      .then(() => {
        setShowDeleteModal(false);
        // Soft delete sets is_active to false
        // Check if the deleted product still matches filters
        const deletedProductUpdated = { ...deletingProduct, is_active: false };

        if (productMatchesFilters(deletedProductUpdated)) {
          // Product still matches filters, update its status
          setProducts((prevProducts) =>
            prevProducts.map((p) =>
              p.id === deletingProduct.id ? { ...p, is_active: false } : p
            )
          );
        } else {
          // Product no longer matches filters, remove it
          setProducts((prevProducts) =>
            prevProducts.filter((p) => p.id !== deletingProduct.id)
          );
        }
        setDeletingProduct(null);
      })
      .catch((err) => {
        console.error("Fout bij verwijderen product:", err);
        setErrorModal({ show: true, message: "Er is een fout opgetreden bij het verwijderen van het product." });
      })
      .finally(() => setLoading(false));
  };

  // Helper function to calculate VAT prices
  const calculateVatPrice = (price, direction) => {
    if (!price || isNaN(parseFloat(price))) return "";
    const numPrice = parseFloat(price);
    if (direction === "excl_to_incl") {
      return (numPrice * 1.21).toFixed(2);
    } else {
      return (numPrice / 1.21).toFixed(2);
    }
  };

  // Handle selling price excl VAT change - auto-calculate incl VAT
  const handleSellingPriceExclVatChange = (value) => {
    setFormData({
      ...formData,
      selling_price_excl_vat: value,
      selling_price_incl_vat: calculateVatPrice(value, "excl_to_incl"),
    });
  };

  // Handle selling price incl VAT change - auto-calculate excl VAT
  const handleSellingPriceInclVatChange = (value) => {
    setFormData({
      ...formData,
      selling_price_incl_vat: value,
      selling_price_excl_vat: calculateVatPrice(value, "incl_to_excl"),
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);

    const method = editingProduct ? "PATCH" : "POST";
    const url = editingProduct ? `/products/${editingProduct.id}` : "/products";

    apiFetch(url, {
      method,
      body: JSON.stringify({
        name: formData.name,
        brand_id: parseInt(formData.brand_id),
        description: formData.description,
        is_to_be_sold: formData.is_to_be_sold,
        selling_price_excl_vat: formData.selling_price_excl_vat ? parseFloat(formData.selling_price_excl_vat) : null,
        selling_price_incl_vat: formData.selling_price_incl_vat ? parseFloat(formData.selling_price_incl_vat) : null,
        purchase_price_excl_discount: formData.purchase_price_excl_discount ? parseFloat(formData.purchase_price_excl_discount) : null,
        purchase_price_incl_discount: formData.purchase_price_incl_discount ? parseFloat(formData.purchase_price_incl_discount) : null,
        is_active: formData.is_active,
      }),
    })
      .then((updatedProduct) => {
        setShowModal(false);
        setFormData({
          name: "",
          brand_id: "",
          description: "",
          is_to_be_sold: false,
          selling_price_excl_vat: "",
          selling_price_incl_vat: "",
          purchase_price_excl_discount: "",
          purchase_price_incl_discount: "",
          is_active: true,
        });

        if (editingProduct) {
          // Check if updated product still matches current filters
          const updatedProductData = {
            is_active: updatedProduct.is_active,
            is_to_be_sold: updatedProduct.is_to_be_sold,
          };

          if (productMatchesFilters(updatedProductData)) {
            // Product still matches filters, update it in the list
            setProducts((prevProducts) =>
              prevProducts.map((p) => {
                if (p.id === editingProduct.id) {
                  const newSellingPrice = updatedProduct.selling_price_incl_vat;
                  const newPurchasePrice = updatedProduct.purchase_price_incl_discount;
                  const newMargin = (newSellingPrice != null && newPurchasePrice != null)
                    ? newSellingPrice - newPurchasePrice
                    : null;
                  return {
                    ...p,
                    product_name: updatedProduct.name,
                    brand_id: updatedProduct.brand_id,
                    brand: updatedProduct.brand,
                    description: updatedProduct.description,
                    is_active: updatedProduct.is_active,
                    is_to_be_sold: updatedProduct.is_to_be_sold,
                    selling_price_incl_vat: newSellingPrice,
                    selling_price_excl_vat: updatedProduct.selling_price_excl_vat,
                    purchase_price_incl_discount: newPurchasePrice,
                    purchase_price_excl_discount: updatedProduct.purchase_price_excl_discount,
                    margin: newMargin,
                  };
                }
                return p;
              })
            );
          } else {
            // Product no longer matches filters, remove it from the list
            setProducts((prevProducts) =>
              prevProducts.filter((p) => p.id !== editingProduct.id)
            );
          }
        } else {
          // For new products, fetch the list to get the complete data including stock info
          fetchProducts();
        }
        setEditingProduct(null);
      })
      .catch((err) => {
        console.error("Fout bij opslaan product:", err);
        setErrorModal({ show: true, message: "Er is een fout opgetreden bij het opslaan van het product." });
        if (err.message.includes("403")) setUnauthorized(true);
      })
      .finally(() => setLoading(false));
  };

  const handleOpenStickersModal = async () => {
    setExportingStickers(true);
    try {
      const params = new URLSearchParams({
        include_inactive: 'false',
        limit: '10000',
      });
      const response = await apiFetch(`/products?${params}`);
      const allProducts = response.items || response;
      setAllProductsForStickers(allProducts);

      // Get unique brands and select all by default
      const brands = [...new Set(allProducts.map(p => p.brand || 'Zonder merk'))].sort((a, b) => {
        if (a === 'Zonder merk') return 1;
        if (b === 'Zonder merk') return -1;
        return a.localeCompare(b);
      });
      const initialSelection = {};
      brands.forEach(brand => {
        initialSelection[brand] = true;
      });
      setSelectedBrands(initialSelection);
      setShowStickersModal(true);
    } catch (err) {
      console.error('Error loading products for stickers:', err);
      setErrorModal({ show: true, message: err.message || 'Fout bij laden van producten' });
    } finally {
      setExportingStickers(false);
    }
  };

  const handleExportStickers = async () => {
    setExportingStickers(true);
    try {
      // Filter by selected brands
      const filteredProducts = allProductsForStickers.filter(p => {
        const brand = p.brand || 'Zonder merk';
        return selectedBrands[brand];
      });
      if (filteredProducts.length === 0) {
        setErrorModal({ show: true, message: 'Geen producten geselecteerd. Selecteer minstens 1 merk.' });
        return;
      }
      await exportProductLabelsToPDF(filteredProducts);
      setShowStickersModal(false);
    } catch (err) {
      console.error('Error exporting product stickers:', err);
      setErrorModal({ show: true, message: err.message || 'Fout bij exporteren van productstickers' });
    } finally {
      setExportingStickers(false);
    }
  };

  if (unauthorized) return <Navigate to="/unauthorized" replace />;

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Producten</h1>
        <div className="flex gap-2">
          <button
            onClick={handleOpenStickersModal}
            disabled={exportingStickers}
            className="flex items-center gap-2 px-4 py-2 border border-blue-600 text-blue-600 rounded hover:bg-blue-50 disabled:opacity-50"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
            </svg>
            {exportingStickers && !showStickersModal ? 'Laden...' : 'Product stickers'}
          </button>
          {canCreate && (
            <button
              onClick={handleAdd}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              + Nieuw Product
            </button>
          )}
        </div>
      </div>

      {/* Search and Filter Bar */}
      <div className="mb-4 flex gap-2 items-center">
        <input
          type="text"
          placeholder="Zoek op productnaam..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full max-w-md px-4 py-2 border rounded"
        />
        <select
          value={brandFilter}
          onChange={(e) => setBrandFilter(e.target.value)}
          className="px-4 py-2 border rounded"
        >
          <option value="">Alle merken</option>
          {Array.isArray(brands) && brands.length > 0 ? (
            brands.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))
          ) : (
            <option disabled>Geen merken gevonden</option>
          )}
        </select>
        <TriStateFilter
          value={toBeSoldFilter}
          onChange={setToBeSoldFilter}
          label="Verkoopsproducten"
          onlyLabel="Alleen verkoopsproducten"
          excludeLabel="Geen verkoopsproducten"
        />
        <TriStateFilter
          value={activeFilter}
          onChange={setActiveFilter}
          label="Actieve producten"
          onlyLabel="Alleen actieve"
          excludeLabel="Alleen inactieve"
        />
        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            className="ml-auto px-4 py-2 border rounded hover:bg-gray-100 text-gray-700"
          >
            Wissen
          </button>
        )}
      </div>

      {/* Loading Indicator */}
      {loadingProducts && (
        <div className="flex justify-center items-center py-12 bg-white shadow rounded-lg">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Producten laden...</span>
        </div>
      )}

      {/* Products Table */}
      {!loadingProducts && (
        <ProductTable
          products={getSortedProducts()}
          onSort={toggleSort}
          sortColumn={sortColumn}
          sortOrder={sortOrder}
          marginSortType={marginSortType}
          onProductDeleted={fetchProducts}
          onEdit={canEdit ? handleEdit : null}
          onDelete={canDelete ? handleDelete : null}
        />
      )}

      {/* Add/Edit Product Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">
              {editingProduct ? "Product Bewerken" : "Nieuw Product"}
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Naam *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Merk *
                </label>
                <select
                  required
                  value={formData.brand_id}
                  onChange={(e) => setFormData({ ...formData, brand_id: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                >
                  <option value="">Selecteer een merk</option>
                  {brands.map((brand) => (
                    <option key={brand.id} value={brand.id}>
                      {brand.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Beschrijving
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                  rows="3"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Aankoopprijs
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">
                      Excl. korting (€)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.purchase_price_excl_discount}
                      onChange={(e) => setFormData({ ...formData, purchase_price_excl_discount: e.target.value })}
                      className="w-full px-3 py-2 border rounded"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">
                      Incl. korting (€)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.purchase_price_incl_discount}
                      onChange={(e) => setFormData({ ...formData, purchase_price_incl_discount: e.target.value })}
                      className="w-full px-3 py-2 border rounded"
                      placeholder="0.00"
                    />
                  </div>
                </div>
              </div>
              <div className="mb-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_to_be_sold}
                    onChange={(e) => setFormData({ ...formData, is_to_be_sold: e.target.checked })}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Verkoopsproduct</span>
                </label>
              </div>
              {formData.is_to_be_sold && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Verkoopprijs
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">
                        Excl. BTW (€)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.selling_price_excl_vat}
                        onChange={(e) => handleSellingPriceExclVatChange(e.target.value)}
                        className="w-full px-3 py-2 border rounded"
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">
                        Incl. 21% BTW (€)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.selling_price_incl_vat}
                        onChange={(e) => handleSellingPriceInclVatChange(e.target.value)}
                        className="w-full px-3 py-2 border rounded"
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                </div>
              )}
              {editingProduct && (
                <div className="mb-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Product is actief</span>
                  </label>
                </div>
              )}
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border rounded hover:bg-gray-100"
                  disabled={loading}
                >
                  Annuleren
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400"
                  disabled={loading}
                >
                  {loading ? "Bezig..." : "Opslaan"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deletingProduct && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Product Verwijderen</h2>
            <p className="mb-6 text-gray-700">
              Weet je zeker dat je het product "<strong>{deletingProduct.product_name}</strong>" wilt verwijderen?
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeletingProduct(null);
                }}
                className="px-4 py-2 border rounded hover:bg-gray-100"
                disabled={loading}
              >
                Annuleren
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:bg-gray-400"
                disabled={loading}
              >
                {loading ? "Bezig..." : "Verwijderen"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Product Stickers Modal */}
      {showStickersModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 max-h-[80vh] flex flex-col">
            <h2 className="text-xl font-bold mb-2">Productstickers Exporteren</h2>
            <p className="text-sm text-gray-600 mb-4">
              {allProductsForStickers.length} actieve producten
            </p>

            <p className="text-sm text-gray-700 mb-3">
              Selecteer de merken waarvan je stickers wilt genereren:
            </p>

            <div className="flex-1 overflow-auto border rounded-lg p-2 mb-4 max-h-64">
              {Object.keys(selectedBrands).length === 0 ? (
                <p className="text-gray-500 text-sm p-2">Geen producten gevonden.</p>
              ) : (
                <>
                  <div className="flex gap-2 mb-2 pb-2 border-b">
                    <button
                      onClick={() => {
                        const allSelected = {};
                        Object.keys(selectedBrands).forEach(b => { allSelected[b] = true; });
                        setSelectedBrands(allSelected);
                      }}
                      className="text-xs text-blue-600 hover:text-blue-800"
                    >
                      Alles selecteren
                    </button>
                    <span className="text-gray-300">|</span>
                    <button
                      onClick={() => {
                        const noneSelected = {};
                        Object.keys(selectedBrands).forEach(b => { noneSelected[b] = false; });
                        setSelectedBrands(noneSelected);
                      }}
                      className="text-xs text-blue-600 hover:text-blue-800"
                    >
                      Niets selecteren
                    </button>
                  </div>
                  {Object.keys(selectedBrands).sort((a, b) => {
                    if (a === 'Zonder merk') return 1;
                    if (b === 'Zonder merk') return -1;
                    return a.localeCompare(b);
                  }).map(brand => {
                    const count = allProductsForStickers.filter(p => (p.brand || 'Zonder merk') === brand).length;
                    return (
                      <label key={brand} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedBrands[brand] || false}
                          onChange={(e) => setSelectedBrands(prev => ({ ...prev, [brand]: e.target.checked }))}
                          className="w-4 h-4 text-blue-600 rounded"
                        />
                        <span className="flex-1">{brand}</span>
                        <span className="text-sm text-gray-500">({count})</span>
                      </label>
                    );
                  })}
                </>
              )}
            </div>

            <div className="text-sm text-gray-600 mb-4">
              Geselecteerd: {Object.values(selectedBrands).filter(Boolean).length} van {Object.keys(selectedBrands).length} merken
              ({allProductsForStickers.filter(p => selectedBrands[p.brand || 'Zonder merk']).length} producten)
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowStickersModal(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
                disabled={exportingStickers}
              >
                Annuleren
              </button>
              <button
                onClick={handleExportStickers}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                disabled={exportingStickers || Object.values(selectedBrands).filter(Boolean).length === 0}
              >
                {exportingStickers ? 'Bezig...' : 'Exporteren'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      <ErrorModal
        show={errorModal.show}
        message={errorModal.message}
        onClose={() => setErrorModal({ show: false, message: "" })}
      />
    </div>
  );
}
